/**
 * Journeys client helper (dashboard copy).
 *
 * Copied from the canonical reference (journeys-work/funnel-client-reference.js). Contract:
 * CODEDESIGN_JOURNEYS_CONTRACTS.md §1–§3. Keep the logic identical across apps; only these differ:
 *   - API_BASE (the dashboard's authenticated API base, VITE_API_BASE_URL: same host as /register, /guest/...),
 *   - APP_NAME,
 *   - getAuthToken() (same source as src/lib/api-client.js: localStorage.auth_token),
 *   - one extra `if (!API_BASE) return;` in trackFunnel (a missing base must never become a relative-URL POST),
 *   - empty `catch (_) {}` blocks carry a comment (repo eslint `no-empty`); no logic change.
 *
 * Hard rules this file exists to enforce:
 *   - never throws, never returns a promise to callers, never blocks UI
 *   - disabled by localStorage.cd_funnel_off === '1'
 *   - anonymous (no token) events are disabled under GPC / DNT
 *   - no new dependencies
 */

// import.meta.env is replaced statically by Vite; the try/catch only matters when this file is loaded
// outside Vite (unit tests), where `import.meta.env` is undefined.
const API_BASE = (() => {
  try {
    return import.meta.env.VITE_API_BASE_URL || '';
  } catch (_) {
    return '';
  }
})();
const APP_NAME = 'dashboard'; // 'dashboard' | 'builder' | 'pricing_iframe' | 'accounts'
const CD_VID_COOKIE = 'cd_vid';
const CD_FT_COOKIE = 'cd_ft';
const KILL_KEY = 'cd_funnel_off';
const ALLOWED_COOKIE_DOMAIN = '.codedesign.ai';

function getAuthToken() {
  // Same source as src/lib/api-client.js: localStorage.auth_token ('' when absent / storage blocked).
  try {
    return window.localStorage.getItem('auth_token') || '';
  } catch (_) {
    return '';
  }
}

function privacyOptOut() {
  try {
    return navigator.globalPrivacyControl === true || navigator.doNotTrack === '1';
  } catch (_) {
    return false;
  }
}

function killed() {
  try {
    return window.localStorage.getItem(KILL_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function uuid() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  } catch (_) {
    /* ignore */
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  } catch (_) {
    return '';
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The anonymous visitor id, or '' when absent/invalid. */
export function getCdVid() {
  const v = readCookie(CD_VID_COOKIE);
  return UUID_RE.test(v) ? v : '';
}

/** Create the first-party anonymous id if missing (unless the visitor opted out of tracking). */
export function ensureCdVid() {
  try {
    if (killed() || privacyOptOut()) return '';
    const existing = getCdVid();
    if (existing) return existing;
    const id = uuid();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const host = window.location.hostname || '';
    const domain = /(^|\.)codedesign\.ai$/i.test(host) ? '; Domain=' + ALLOWED_COOKIE_DOMAIN : '';
    document.cookie = CD_VID_COOKIE + '=' + id + '; Path=/; Max-Age=31536000; SameSite=Lax' + secure + domain;
    return getCdVid();
  } catch (_) {
    return '';
  }
}

/** First-touch attribution from the cd_ft cookie (set by the landing page) or null. */
export function readFirstTouch() {
  try {
    const raw = readCookie(CD_FT_COOKIE);
    if (!raw) return null;
    const ft = JSON.parse(raw);
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'ttclid', 'referrer', 'referring_domain', 'landing_path'];
    const out = {};
    keys.forEach(function (k) {
      if (ft && typeof ft[k] === 'string' && ft[k]) out[k] = ft[k].slice(0, 200);
    });
    return Object.keys(out).length ? out : null;
  } catch (_) {
    return null;
  }
}

/** Context for signup/login bodies (accounts app). Always safe to spread into a request body. */
export function readAnalyticsContext() {
  try {
    const ctx = {};
    const vid = getCdVid();
    if (vid) ctx.cdVid = vid;
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) ctx.tz = String(tz).slice(0, 64);
    } catch (_) {
    /* ignore */
  }
    try {
      if (navigator.language) ctx.lang = String(navigator.language).slice(0, 16);
    } catch (_) {
    /* ignore */
  }
    const ft = readFirstTouch();
    if (ft) ctx.firstTouch = ft;
    return ctx;
  } catch (_) {
    return {};
  }
}

function dedupeHit(key) {
  try {
    const k = 'cdf:' + key;
    if (window.sessionStorage.getItem(k)) return true;
    window.sessionStorage.setItem(k, '1');
    return false;
  } catch (_) {
    return false;
  }
}

/**
 * Fire-and-forget funnel event.
 * @param {string} name  allowed event name (contract §1)
 * @param {object} fields  { mode, aiLogId, projectId, templateId, attemptId, props, path, dedupe }
 *        fields.dedupe: optional string; when given, the event fires at most once per browser session per key.
 */
export function trackFunnel(name, fields) {
  try {
    if (!name || killed()) return;
    if (!API_BASE) return;
    const f = fields || {};
    const token = getAuthToken();
    if (!token && privacyOptOut()) return;
    if (f.dedupe && dedupeHit(name + ':' + f.dedupe)) return;

    const body = {
      name: String(name),
      clientEventId: uuid(),
      app: APP_NAME,
      occurredAt: new Date().toISOString(),
      path: (f.path || (window.location && window.location.pathname) || '').slice(0, 200),
    };
    ['mode', 'aiLogId', 'projectId', 'templateId', 'attemptId'].forEach(function (k) {
      if (f[k]) body[k] = String(f[k]).slice(0, 200);
    });
    const vid = getCdVid();
    if (vid) body.anonymousId = vid;
    if (f.props && typeof f.props === 'object') {
      const props = {};
      let n = 0;
      Object.keys(f.props).forEach(function (k) {
        const v = f.props[k];
        if (n >= 12 || !/^[a-zA-Z][a-zA-Z0-9_]{0,39}$/.test(k)) return;
        if (typeof v === 'string') props[k] = v.slice(0, 200);
        else if (typeof v === 'number' && isFinite(v)) props[k] = v;
        else if (typeof v === 'boolean') props[k] = v;
        else return;
        n += 1;
      });
      body.props = props;
    }

    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['x-access-token'] = token;
    const p = fetch(String(API_BASE).replace(/\/+$/, '') + '/funnel/event', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'omit',
    });
    if (p && typeof p.catch === 'function') p.catch(function () {});
  } catch (_) {
    /* analytics must never break the app */
  }
}
